import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    AlertTriangle,
    Check,
    Clock,
    History,
    Loader2,
    RotateCcw,
    SlidersHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import {
    getOrchestratorSettingHistory,
    listOrchestratorSettings,
    updateOrchestratorSetting,
} from '@/services/api/connections';
import type { OrchestratorSetting } from '../types/orchestratorSettings.types';

/**
 * Configs operacionais do orquestrador de broadcast.
 *
 * Estas flags mudam o comportamento do envio em PRODUÇÃO em tempo real —
 * antes disso viviam como env var no docker-compose e mudar uma exigia
 * acesso SSH ao servidor + recriar os containers (feito ~6x num único dia
 * de incidente, com o envio parado no meio).
 *
 * A lista não é hardcoded: tipo, limites e descrição vêm do backend, então
 * uma flag nova aparece aqui sem mexer neste arquivo.
 */
export default function OrchestratorSettingsPanel() {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [drafts, setDrafts] = useState<Record<string, string>>({});
    const [historyKey, setHistoryKey] = useState<string | null>(null);
    const [detailKey, setDetailKey] = useState<string | null>(null);
    // null = ainda não escolheu; cai na primeira aba que o backend mandar.
    const [activeGroup, setActiveGroup] = useState<string | null>(null);

    const { data, isLoading } = useQuery({
        queryKey: ['orchestrator-settings'],
        queryFn: listOrchestratorSettings,
    });

    const { data: history, isLoading: loadingHistory } = useQuery({
        queryKey: ['orchestrator-settings-history', historyKey],
        queryFn: () => getOrchestratorSettingHistory(historyKey as string),
        enabled: !!historyKey,
    });

    const saveMutation = useMutation({
        mutationFn: ({ key, value }: { key: string; value: string }) =>
            updateOrchestratorSetting(key, value),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['orchestrator-settings'] });
            queryClient.invalidateQueries({ queryKey: ['orchestrator-settings-history'] });
            setDrafts((prev) => {
                const next = { ...prev };
                delete next[res.key_name];
                return next;
            });
            toast({
                title: 'Configuração salva',
                description: res.is_hot
                    ? `Passa a valer em até ${res.applies_in_seconds}s, sem reiniciar nada.`
                    : 'Só passa a valer no próximo restart do serviço.',
            });
        },
        onError: (err: unknown) => {
            // A mensagem do backend é escrita para ser lida pelo operador
            // (diz o range aceito e o que foi recebido) — mostrar ela crua
            // é melhor que um texto genérico.
            const detail =
                (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
            toast({
                variant: 'destructive',
                title: 'Não foi possível salvar',
                description: detail || 'Erro ao salvar a configuração.',
            });
        },
    });

    const appliesIn = data?.applies_in_seconds ?? 10;

    const handleToggle = (setting: OrchestratorSetting, checked: boolean) => {
        saveMutation.mutate({ key: setting.key_name, value: checked ? 'true' : 'false' });
    };

    const draftFor = (s: OrchestratorSetting) => drafts[s.key_name] ?? s.value;
    const isDirty = (s: OrchestratorSetting) =>
        drafts[s.key_name] !== undefined && drafts[s.key_name] !== s.value;

    const rangeLabel = (s: OrchestratorSetting) => {
        if (s.min_value === null && s.max_value === null) return null;
        if (s.min_value !== null && s.max_value !== null) return `${s.min_value} a ${s.max_value}`;
        if (s.min_value !== null) return `mínimo ${s.min_value}`;
        return `máximo ${s.max_value}`;
    };

    const formatWhen = (iso: string | null) => {
        if (!iso) return null;
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? null : d.toLocaleString('pt-BR');
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    const settings = data?.settings ?? [];
    const groups = data?.groups ?? [];

    // Grupo que o backend não mandou não some da tela — vira aba própria no
    // fim. Só acontece se alguém criar a flag e esquecer de classificar.
    const gruposConhecidos = new Set(groups.map((g) => g.id));
    const avulsos = [...new Set(settings.map((s) => s.group).filter((g) => !gruposConhecidos.has(g)))];
    const abas = [
        ...groups,
        ...avulsos.map((id) => ({ id, label: id, description: '' })),
    ];

    const grupoAtivo = activeGroup && abas.some((g) => g.id === activeGroup)
        ? activeGroup
        : abas[0]?.id ?? null;
    const abaAtual = abas.find((g) => g.id === grupoAtivo) ?? null;
    const visiveis = settings.filter((s) => s.group === grupoAtivo);

    const contarNoGrupo = (id: string) => settings.filter((s) => s.group === id).length;
    const ligadasNoGrupo = (id: string) =>
        settings.filter(
            (s) => s.group === id && s.value_type === 'bool' && s.value.toLowerCase() === 'true',
        ).length;
    const boolsNoGrupo = (id: string) =>
        settings.filter((s) => s.group === id && s.value_type === 'bool').length;

    const ultimaAlteracao = settings
        .map((s) => s.updated_at)
        .filter((d): d is string => !!d)
        .sort()
        .at(-1);
    const naoQuentes = settings.filter((s) => !s.is_hot).length;

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-semibold text-gray-900">Orquestrador</h2>
                <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                    Configurações do motor de envio, aplicadas em produção sem reiniciar nada.
                    Alterações valem em até {appliesIn} segundos.
                </p>
            </div>

            {settings.length === 0 && (
                <div className="rounded-xl border border-gray-200 bg-white p-5 text-sm text-gray-500">
                    Nenhuma configuração disponível.
                </div>
            )}

            {settings.length > 0 && (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                            <p className="text-[11px] uppercase tracking-wider text-gray-400">
                                Configurações
                            </p>
                            <p className="text-xl font-semibold text-gray-900 tabular-nums">
                                {settings.length}
                            </p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                            <p className="text-[11px] uppercase tracking-wider text-gray-400">
                                Grupos
                            </p>
                            <p className="text-xl font-semibold text-gray-900 tabular-nums">
                                {abas.length}
                            </p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                            <p className="text-[11px] uppercase tracking-wider text-gray-400">
                                Última alteração
                            </p>
                            <p className="text-sm font-medium text-gray-900 mt-1">
                                {formatWhen(ultimaAlteracao ?? null) ?? '—'}
                            </p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                            <p className="text-[11px] uppercase tracking-wider text-gray-400">
                                Exigem restart
                            </p>
                            <p
                                className={`text-xl font-semibold tabular-nums ${
                                    naoQuentes > 0 ? 'text-amber-700' : 'text-gray-900'
                                }`}
                            >
                                {naoQuentes}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-px">
                        {abas.map((g) => {
                            const ativa = g.id === grupoAtivo;
                            const bools = boolsNoGrupo(g.id);
                            return (
                                <button
                                    key={g.id}
                                    type="button"
                                    onClick={() => setActiveGroup(g.id)}
                                    className={`relative -mb-px rounded-t-lg border border-b-0 px-4 py-2.5 text-sm transition-colors ${
                                        ativa
                                            ? 'border-gray-200 bg-white font-semibold text-gray-900'
                                            : 'border-transparent text-gray-500 hover:text-gray-900'
                                    }`}
                                >
                                    {g.label}
                                    <span className="ml-2 rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500 tabular-nums">
                                        {bools > 0
                                            ? `${ligadasNoGrupo(g.id)}/${bools} on`
                                            : contarNoGrupo(g.id)}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {abaAtual?.description && (
                        <p className="text-sm text-gray-600 max-w-3xl leading-relaxed -mt-2">
                            {abaAtual.description}
                        </p>
                    )}
                </>
            )}

            {visiveis.map((s) => {
                const when = formatWhen(s.updated_at);
                const range = rangeLabel(s);
                const isBool = s.value_type === 'bool';
                const isNumber = s.value_type === 'int' || s.value_type === 'float';
                const saving = saveMutation.isPending && saveMutation.variables?.key === s.key_name;

                return (
                    <div key={s.key_name} className="rounded-xl border border-gray-200 bg-white p-5">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3 min-w-0">
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0f172a]/5 text-[#0f172a]">
                                    <SlidersHorizontal className="h-4 w-4" />
                                </span>
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-gray-900">{s.label}</p>
                                    <code className="mt-0.5 inline-block rounded bg-gray-50 px-1.5 py-0.5 text-[11px] text-gray-400">
                                        {s.key_name}
                                    </code>
                                    <p className="text-xs text-gray-600 mt-1.5 max-w-xl leading-relaxed">
                                        {detailKey === s.key_name ? s.description : s.summary || s.description}
                                    </p>
                                    {s.summary && s.description !== s.summary && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setDetailKey(detailKey === s.key_name ? null : s.key_name)
                                            }
                                            className="mt-1 text-[11px] text-gray-500 underline underline-offset-2 hover:text-gray-900 transition-colors"
                                        >
                                            {detailKey === s.key_name
                                                ? 'ocultar detalhes'
                                                : 'ver detalhes e histórico da decisão'}
                                        </button>
                                    )}

                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                                        {range && (
                                            <span className="text-[11px] text-gray-400">
                                                Aceita: {range}
                                            </span>
                                        )}
                                        {when && (
                                            <span className="text-[11px] text-gray-400">
                                                Alterado em {when}
                                                {s.updated_by ? ` por ${s.updated_by}` : ''}
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setHistoryKey(historyKey === s.key_name ? null : s.key_name)
                                            }
                                            className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-900 transition-colors"
                                        >
                                            <History className="h-3 w-3" />
                                            {historyKey === s.key_name ? 'Ocultar histórico' : 'Histórico'}
                                        </button>
                                    </div>

                                    {!s.is_hot && (
                                        <div className="flex items-start gap-1.5 mt-2 text-[11px] text-amber-700">
                                            <AlertTriangle className="h-3 w-3 mt-px shrink-0" />
                                            <span>Só passa a valer no próximo restart do serviço.</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="shrink-0">
                                {isBool && (
                                    <Switch
                                        checked={s.value.toLowerCase() === 'true'}
                                        disabled={saving}
                                        onCheckedChange={(checked) => handleToggle(s, checked)}
                                    />
                                )}

                                {!isBool && (
                                    <div className="flex items-center gap-2">
                                        <Input
                                            type={isNumber ? 'number' : 'text'}
                                            value={draftFor(s)}
                                            min={s.min_value ?? undefined}
                                            max={s.max_value ?? undefined}
                                            disabled={saving}
                                            onChange={(e) =>
                                                setDrafts((prev) => ({
                                                    ...prev,
                                                    [s.key_name]: e.target.value,
                                                }))
                                            }
                                            className="w-28 h-9 text-sm"
                                        />
                                        {isDirty(s) && (
                                            <>
                                                <Button
                                                    size="sm"
                                                    className="h-9"
                                                    disabled={saving}
                                                    onClick={() =>
                                                        saveMutation.mutate({
                                                            key: s.key_name,
                                                            value: draftFor(s),
                                                        })
                                                    }
                                                >
                                                    {saving ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        <Check className="h-4 w-4" />
                                                    )}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-9"
                                                    disabled={saving}
                                                    onClick={() =>
                                                        setDrafts((prev) => {
                                                            const next = { ...prev };
                                                            delete next[s.key_name];
                                                            return next;
                                                        })
                                                    }
                                                >
                                                    <RotateCcw className="h-4 w-4" />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {historyKey === s.key_name && (
                            <div className="mt-4 border-t border-gray-100 pt-4">
                                {loadingHistory && (
                                    <div className="flex items-center gap-2 text-xs text-gray-400">
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                        Carregando histórico...
                                    </div>
                                )}
                                {!loadingHistory && (history?.history?.length ?? 0) === 0 && (
                                    <p className="text-xs text-gray-400">
                                        Nenhuma alteração registrada.
                                    </p>
                                )}
                                {!loadingHistory && (history?.history?.length ?? 0) > 0 && (
                                    <ul className="space-y-2">
                                        {history!.history.map((h) => (
                                            <li
                                                key={h.id}
                                                className="flex items-start gap-2 text-xs text-gray-600"
                                            >
                                                <Clock className="h-3 w-3 mt-0.5 shrink-0 text-gray-400" />
                                                <span>
                                                    <span className="text-gray-400">
                                                        {formatWhen(h.changed_at)}
                                                    </span>{' '}
                                                    — <span className="font-medium">{h.changed_by}</span>{' '}
                                                    alterou de{' '}
                                                    <code className="rounded bg-gray-100 px-1">
                                                        {h.old_value ?? '—'}
                                                    </code>{' '}
                                                    para{' '}
                                                    <code className="rounded bg-gray-100 px-1">
                                                        {h.new_value}
                                                    </code>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
